import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// Firestore imports removed
import { ApiService } from "../../services/api_service";
import "./checkout_ticket.css";

// Components
import Navbar from "../../common_components/navbar/Navbar";
import PaymentLoadingModal from "../../common_components/PaymentLoadingModal";
import PaymentSuccessModal from "../../common_components/PaymentSuccessModal";
import CouponInput from "../../common_components/CouponInput";

// Utils
import { formatFirestoreDate } from "../../utils/date_formatter";
import { convertTo12HourFormat } from "../../utils/time_formatter";
import { handlePaymentSuccess } from "../../utils/paymentHandler";
import { getBookingConfirmationEmailTemplate, getBookingConfirmationTextTemplate } from "../../utils/emailTemplates";

// Hooks
import useAuth from "../../hooks/use_auth";

// Assets
import Ticket from "../../Assets/Ticketavailableicon.svg";
import arrowIcon from "../../Assets/arrow_buttoncheckout.svg";
import arrowDown from '../../Assets/dropdown.svg';

// Indian states list
const indianStates = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry'
];

const Checkout = () => {
  // ============================================================================
  // HOOKS & STATE MANAGEMENT
  // ============================================================================

  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth();

  // Extract data from route state
  const {
    eventId,
    selectedSlot,
    selectedTickets,
    totalPrice,
    totalTickets,
    eventName,
    eventLocation,
    eventData,
    selectedDate,
  } = location.state || {};

  // UI State
  const [showFeeDetails, setShowFeeDetails] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Payment State
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'error', null
  const [bookingId, setBookingId] = useState(null);
  const [paymentError, setPaymentError] = useState(null); // For showing payment errors on screen

  // User Data Loading State
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [userDataError, setUserDataError] = useState(null);

  // Edit State - tracks which field is being edited
  const [editingField, setEditingField] = useState(null);

  // Coupon State
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Form State - Initialize with empty values
  const [ticketDetails, setTicketDetails] = useState({
    name: "",
    email: "",
    phone: "",
    state: "",
  });

  // ============================================================================
  // USER DATA FETCHING
  // ============================================================================

  // Fetch user data from API
  const fetchUserData = async () => {
    if (!user?.uid) {
      setUserDataError("User not authenticated");
      setUserDataLoading(false);
      return;
    }

    try {
      setUserDataLoading(true);
      setUserDataError(null);

      const response = await ApiService.getSingleUser(user.uid);
      // Handle response structure (check if data is nested or direct)
      // Assuming response.data or response is the user map
      // Previous code checked if snap.exists(). API errors if not found usually.

      const userData = response.data || response;

      if (userData) {
        const newTicketDetails = {
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          state: userData.state || "",
        };

        setTicketDetails(newTicketDetails);
      } else {
        setUserDataError("User data not found");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      // Check if error is 404
      setUserDataError("Failed to load user data");
    } finally {
      setUserDataLoading(false);
    }
  };

  // Fetch user data when component mounts or user changes his or her data
  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      setUserDataLoading(false);
      // Try to restore form data from sessionStorage if user was redirected to login, need to be login when the user wants to book ticket already, and if not, redirect to login first
      const savedData = sessionStorage.getItem('checkoutFormData');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          if (parsed.ticketDetails) {
            setTicketDetails(parsed.ticketDetails);
          }
          sessionStorage.removeItem('checkoutFormData');
        } catch (e) {
          console.error('Error restoring form data:', e);
        }
      }
    }
  }, [user]);

  // ============================================================================
  // FORM VALIDATION
  // ============================================================================

  const validateTicketDetails = () => {
    const errors = [];

    if (!ticketDetails.name?.trim()) {
      errors.push("Name is required");
    }

    if (!ticketDetails.email?.trim()) {
      errors.push("Email is required");
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(ticketDetails.email)) {
        errors.push("Please enter a valid email address");
      }
    }

    if (!ticketDetails.phone?.trim()) {
      errors.push("Phone number is required");
    } else {
      // Basic phone validation (Indian format), consider the logged in mobile number bydefault, until the user changes himself
      const phoneRegex = /^(\+91[\-\s]?)?[0]?(91)?[6789]\d{9}$/;
      if (!phoneRegex.test(ticketDetails.phone.replace(/[\s\-\(\)]/g, ""))) {
        errors.push("Please enter a valid phone number");
      }
    }

    if (!ticketDetails.state?.trim()) {
      errors.push("State is required");
    }

    return errors;
  };

  // ============================================================================
  // CALCULATIONS
  // ============================================================================

  // Calculate fees dynamically, depending upon the percentage value finalised with the client
  const subtotal = parseFloat(totalPrice) || 0;
  const platformFeeRate = eventData?.platformFeeRate ?? 5; // Default to 5% if missing
  const convenienceFee = (subtotal * platformFeeRate) / 100;
  const igst = convenienceFee * 0.18;

  // Apply coupon discount if available, for example student or first order discount
  const couponDiscount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  const discountedSubtotal = Math.max(0, subtotal - couponDiscount);

  // Recalculate fees based on discounted subtotal
  const adjustedConvenienceFee = (discountedSubtotal * platformFeeRate) / 100;
  const adjustedIgst = adjustedConvenienceFee * 0.18;
  const grandTotal = discountedSubtotal + adjustedConvenienceFee + adjustedIgst;

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  // Handle field click to start editing
  const handleFieldClick = (fieldName) => {
    if (!userDataLoading) {
      setEditingField(fieldName);
    }
  };

  // Handle field blur to stop editing
  const handleFieldBlur = () => {
    setEditingField(null);
  };

  // Handle Enter key to stop editing
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setEditingField(null);
    }
  };

  // ============================================================================
  // COUPON HANDLERS
  // ============================================================================

  const handleCouponApplied = (couponData) => {
    setAppliedCoupon(couponData);
    console.log('Coupon applied:', couponData);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
    console.log('Coupon removed');
  };

  // integrating the payment gateway for the live mode
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // ============================================================================
  // PAYMENT HANDLING
  // ============================================================================

  const sendConfirmationEmail = async (bookingId, bookingData) => {
    try {
      // Use the deployed Firebase Cloud Function for sending emails
      const emailFunctionUrl = process.env.REACT_APP_EMAIL_FUNCTION_URL_DEV;

      const emailData = {
        to: [bookingData.ticketDetails.email],
        from: "tickets@fusshn.com",
        subject: `🎫 Booking Confirmed - ${bookingData.eventName}`,
        html: getBookingConfirmationEmailTemplate(bookingId, bookingData),
        text: getBookingConfirmationTextTemplate(bookingId, bookingData)
      };

      const response = await fetch(emailFunctionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(emailData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log("✅ Confirmation email sent successfully");
      } else {
        console.error("❌ Failed to send confirmation email:", result.error?.message);
      }
    } catch (error) {
      console.error("❌ Error sending confirmation email:", error);
      // Don't throw error - email failure shouldn't block the booking success flow
    }
  };

  const createOrderApiCall = async () => {
    setShowPaymentModal(true); // Show payment confirmation modal
    setPaymentError(null); // Clear any previous payment errors

    // Use the deployed Firebase Cloud Function
    // Try specific function URL first, then fall back to base URL + function name
    const cloudFunctionUrl = process.env.REACT_APP_RAZORPAY_FUNCTION_URL_DEV;

    // Create notes object and filter out empty values
    const notesData = {
      event_id: eventId,
      event_name: eventName,
      slot_id: selectedSlot?.id,
      total_tickets: totalTickets,
      user_email: ticketDetails?.email,
      user_name: ticketDetails?.name
    };

    // Filter out undefined, null, or empty string values
    const filteredNotes = Object.fromEntries(
      Object.entries(notesData)
        .filter(([key, value]) => value != null && value !== '' && value !== undefined)
        .map(([key, value]) => [key, String(value)])
    );

    const requestBody = {
      amount: Math.round(grandTotal * 100), // Convert to paise
      currency: "INR",
      receipt: `ticket_${Date.now()}_${eventId || 'unknown'}`.substring(0, 40), // Ensure max 40 chars
      notes: filteredNotes
    };

    const requestHeaders = {
      "Content-Type": "application/json"
    };

    try {
      const res = await fetch(cloudFunctionUrl, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Extract order ID from the structured response
        const orderID = data.data.id;
        handlePayment(orderID);
      } else {
        setShowPaymentModal(false); // Hide modal on error
        setIsProcessingPayment(false); // Stop processing state

        // Show error message on screen instead of alert
        const errorMessage = data.error?.message || "Failed to create order. Please try again.";
        setPaymentError(errorMessage);
      }
    } catch (error) {
      setShowPaymentModal(false); // Hide modal on error
      setIsProcessingPayment(false); // Stop processing state
      setPaymentError("Failed to create order. Please check your connection.");
    }
  };

  const handlePayment = async (orderID) => {
    const res = await loadRazorpayScript();

    if (!res) {
      setShowPaymentModal(false);
      setIsProcessingPayment(false);
      setPaymentError("Razorpay SDK failed to load. Please check your internet connection.");
      return;
    }

    const razorpayKey = process.env.REACT_APP_RAZORPAY_API_KEY_DEV;

    if (!razorpayKey) {
      setShowPaymentModal(false);
      setIsProcessingPayment(false);
      setPaymentError("Razorpay key is missing. Please check environment configuration.");
      return;
    }

    const options = {
      key: razorpayKey,
      amount: Math.round(grandTotal * 100), // Amount in paise
      currency: "INR",
      name: "Fusshn",
      description: `${eventName} - Ticket Booking`,
      image: "https://firebasestorage.googleapis.com/v0/b/fusshn-aef8e.appspot.com/o/app_logo%2Fapp_icon.png?alt=media&token=a5953a7e-65fb-4d5e-baa5-1cb5f1723556",
      order_id: orderID,
      handler: async function (response) {
        // Validate required data before proceeding
        if (!eventId || !selectedSlot || !selectedTickets || !selectedDate) {
          setShowPaymentModal(false);
          setIsProcessingPayment(false);
          setPaymentError("Booking data is missing. Please try again.");
          return;
        }

        // Prepare checkout data for payment handler
        const checkoutData = {
          eventId,
          selectedSlot,
          selectedTickets,
          selectedDate,
          eventData,
          totalPrice: parseFloat(totalPrice),
          grandTotal: parseFloat(grandTotal.toFixed(2)),
          ticketDetails,
          eventName,
          eventLocation,
          appliedCoupon: appliedCoupon ? {
            couponCode: appliedCoupon.couponCode,
            discountAmount: appliedCoupon.discountAmount,
            discountType: appliedCoupon.discountType,
            discountValue: appliedCoupon.discountValue
          } : null
        };

        // Handle payment success with our utility function
        try {
          await handlePaymentSuccess(
            response.razorpay_payment_id,
            checkoutData,
            user,
            setShowPaymentModal,
            // Success callback
            (generatedBookingId) => {
              setPaymentStatus('success');
              setBookingId(generatedBookingId);

              // 📧 Send confirmation email
              sendConfirmationEmail(generatedBookingId, checkoutData);

              // Redirect to booking confirmation after 2 seconds
              setTimeout(() => {
                navigate(`/my-bookings`);
              }, 2000);
            },
            // Error callback
            (errorMessage) => {
              setPaymentStatus('error');
              setPaymentError(`Booking Error: ${errorMessage}`);
            }
          );
        } catch (error) {
          setShowPaymentModal(false);
          setIsProcessingPayment(false);
          setPaymentError("Failed to process booking. Please contact support.");
        }
      },
      modal: {
        ondismiss: function () {
          // 🔴 This is where you handle dismissal
          setShowPaymentModal(false); // Hide modal when dismissed
          setIsProcessingPayment(false); // Stop processing state
          setPaymentError("Payment was cancelled. You can try again when ready.");
        }
      },
      prefill: {
        name: ticketDetails.name,
        email: ticketDetails.email,
        contact: ticketDetails.phone,
      },
      notes: {
        event_id: eventId,
        slot_id: selectedSlot.id,
        tickets: JSON.stringify(selectedTickets),
      },
      theme: {
        color: "#11111",
      },
    };

    const rzp = new window.Razorpay(options);

    // Payment Event Handlers
    rzp.on("payment.failed", function (response) {
      setShowPaymentModal(false); // Hide modal on failure
      setIsProcessingPayment(false); // Stop processing state
      setPaymentError("Payment failed: " + response.error.description);
    });

    // Handle when user closes the payment modal without completing payment
    // rzp.on("payment.dismissed", function () {
    //   setShowPaymentModal(false); // Hide modal when dismissed
    //   setIsProcessingPayment(false); // Stop processing state
    //   setPaymentError("Payment was cancelled. You can try again when ready.");
    // });

    // Add a timeout fallback to ensure modal gets dismissed even if events don't fire
    const modalTimeoutId = setTimeout(() => {
      setShowPaymentModal(false);
      setIsProcessingPayment(false);
      setPaymentError("Payment session expired. Please try again.");
    }, 300000); // 5 minutes timeout

    // Clear timeout if payment completes successfully
    const originalHandler = options.handler;
    options.handler = async function (response) {
      clearTimeout(modalTimeoutId);
      await originalHandler(response);
    };

    // Track if payment is still in progress
    let paymentInProgress = true;

    // Override the close method to ensure our state gets updated
    const originalClose = rzp.close;
    rzp.close = function () {
      if (paymentInProgress) {
        paymentInProgress = false;
        setShowPaymentModal(false);
        setIsProcessingPayment(false);
        setPaymentError("Payment was cancelled. You can try again when ready.");
      }
      originalClose.call(this);
    };

    // Ensure payment in progress flag is cleared on success
    const originalSuccessHandler = options.handler;
    options.handler = async function (response) {
      paymentInProgress = false;
      clearTimeout(modalTimeoutId);
      await originalSuccessHandler(response);
    };

    rzp.open();

    // Additional safety check - monitor for modal closure
    const checkModalClosure = setInterval(() => {
      const razorpayFrame = document.querySelector('#razorpay-container iframe');
      const razorpayModal = document.querySelector('.razorpay-overlay');

      if (paymentInProgress && !razorpayFrame && !razorpayModal && showPaymentModal) {
        // Modal was closed but our state wasn't updated
        clearInterval(checkModalClosure);
        paymentInProgress = false;
        setShowPaymentModal(false);
        setIsProcessingPayment(false);
        setPaymentError("Payment was cancelled. You can try again when ready.");
      }

      // Stop checking after 30 seconds
      if (!paymentInProgress) {
        clearInterval(checkModalClosure);
      }
    }, 1000);

    // Clean up interval after 30 seconds
    setTimeout(() => {
      clearInterval(checkModalClosure);
    }, 30000);
  };

  const handleProceed = async () => {
    // 🔄 Reset all UI states first
    setPaymentError(null);
    setPaymentStatus(null);
    setBookingId(null);
    setShowPaymentModal(false);
    setIsProcessingPayment(false);

    // Validate ticket details first (before checking login)
    const errors = validateTicketDetails();
    if (errors.length > 0) {
      setPaymentError("Please complete all required fields:\n• " + errors.join("\n• "));
      return;
    }

    // After validation, check if user is authenticated - redirect to login if not
    if (!user) {
      // Store form data in sessionStorage to restore after login
      sessionStorage.setItem('checkoutFormData', JSON.stringify({
        ticketDetails,
        eventId,
        selectedSlot,
        selectedTickets,
        selectedDate,
        totalPrice,
        totalTickets,
        eventName,
        eventLocation,
        eventData
      }));
      navigate("/login", { state: { from: 'checkout' } });
      return;
    }

    // Check if user data is still loading
    if (userDataLoading) {
      setPaymentError("Please wait while we load your details...");
      return;
    }

    setIsProcessingPayment(true);
    try {
      await createOrderApiCall();
    } catch (error) {
      setPaymentError("Failed to initiate payment. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  // ============================================================================
  // ERROR HANDLING
  // ============================================================================

  // If no data is passed, show error
  if (!eventId || !selectedSlot) {
    return (
      <>
        <Navbar />
        <div className="checkout-container px-4 md:px-0">
          <div className="error-message text-center py-8">
            <h3 className="text-lg md:text-xl mb-4">No booking data found</h3>
            <p className="text-sm md:text-base">Please go back and select your tickets again.</p>
          </div>
        </div>
      </>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <Navbar hideBorder />
      <div className="checkout-title px-4 md:px-0 pt-[85px] sm:pt-[88px] lg:pt-[92px]">Checkout</div>
      <div className="checkout-container px-4 md:px-0">
        <div className="checkout-box !flex !flex-col lg:!flex-row !gap-6 lg:!gap-8">

          {/* ====================================================================== */}
          {/* LEFT SECTION */}
          {/* ====================================================================== */}
          <div className="checkout-left-section !w-full lg:!w-1/2 order-2 lg:order-1">
            {/* Important Points - Fetch from database if available */}
            {(() => {
              const importantPoints = eventData?.importantPoints || eventData?.instructions || [];
              const pointsArray = Array.isArray(importantPoints) ? importantPoints : (importantPoints ? [importantPoints] : []);
              if (pointsArray.length > 0) {
                return (
                  <div className="important-points mb-6 hidden lg:block">
                    <h4 className="text-base md:text-lg mb-3">Important Points</h4>
                    {pointsArray.map((point, index) => (
                      <div key={index} className="point mb-2 text-sm md:text-base">
                        <span className="bullet mr-2">&#8226;</span>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            })()}

            <div className="ticket-details-box mb-6 hidden lg:block">
              <div className="ticket-details-header mb-4">
                <h4 className="text-base md:text-lg">Ticket Details</h4>
              </div>

              {userDataLoading ? (
                <div className="p-4 md:p-5 text-center text-gray-500 italic text-sm md:text-base">
                  Loading your details...
                </div>
              ) : userDataError ? (
                <div className="p-4 md:p-5 text-center text-red-600 text-sm mb-4">
                  <p>⚠️ {userDataError}</p>
                  <p className="text-xs md:text-sm mt-2">
                    Please fill in your details below
                  </p>
                </div>
              ) : null}

              <div className="ticket-form-fields grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Name Field */}
                <div
                  className={`w-full p-3 !bg-black/40 rounded-md text-sm min-h-5 box-border ${userDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => handleFieldClick('name')}
                >
                  {editingField === 'name' ? (
                    <input
                      type="text"
                      value={ticketDetails.name}
                      onChange={(e) => setTicketDetails({ ...ticketDetails, name: e.target.value })}
                      onBlur={handleFieldBlur}
                      onKeyPress={handleKeyPress}
                      autoFocus
                      className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-0 m-0 !text-white"
                    />
                  ) : (
                    <span className={ticketDetails.name ? 'text-white' : 'text-gray-300'}>
                      {ticketDetails.name || 'Click to enter Full Name *'}
                    </span>
                  )}
                </div>

                {/* Email Field */}
                <div
                  className={`w-full p-3 !bg-black/40 rounded-md text-sm min-h-5 box-border ${userDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => handleFieldClick('email')}
                >
                  {editingField === 'email' ? (
                    <input
                      type="email"
                      value={ticketDetails.email}
                      onChange={(e) => setTicketDetails({ ...ticketDetails, email: e.target.value })}
                      onBlur={handleFieldBlur}
                      onKeyPress={handleKeyPress}
                      autoFocus
                      className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-0 m-0 !text-white"
                    />
                  ) : (
                    <span className={ticketDetails.email ? 'text-white' : 'text-gray-300'}>
                      {ticketDetails.email || 'Click to enter Email Address *'}
                    </span>
                  )}
                </div>

                {/* Phone Field */}
                <div
                  className={`w-full p-3 !bg-black/40 rounded-md text-sm min-h-5 box-border ${userDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => handleFieldClick('phone')}
                >
                  {editingField === 'phone' ? (
                    <input
                      type="tel"
                      value={ticketDetails.phone}
                      onChange={(e) => setTicketDetails({ ...ticketDetails, phone: e.target.value })}
                      onBlur={handleFieldBlur}
                      onKeyPress={handleKeyPress}
                      autoFocus
                      className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-0 m-0 !text-white"
                    />
                  ) : (
                    <span className={ticketDetails.phone ? 'text-white' : 'text-gray-300'}>
                      {ticketDetails.phone || 'Click to enter Phone Number *'}
                    </span>
                  )}
                </div>

                {/* State Field */}
                <div className="w-full !bg-black/40 rounded-md text-sm min-h-5 box-border">
                  <select
                    value={ticketDetails.state}
                    onChange={(e) => setTicketDetails({ ...ticketDetails, state: e.target.value })}
                    disabled={userDataLoading}
                    className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-3 m-0 !text-white rounded-md cursor-pointer disabled:cursor-not-allowed"
                  >
                    <option value="" className="!bg-gray-800 !text-white">Select State *</option>
                    {indianStates.map((state) => (
                      <option key={state} value={state} className="!bg-gray-800 !text-white">
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* ====================================================================== */}
          {/* RIGHT SECTION */}
          {/* ====================================================================== */}
          <div className="checkout-right-section !w-full lg:!w-1/2 order-1 lg:order-2">
            <div className="event-summary-box mb-4 md:mb-6 p-4">
              <h4 className="text-lg md:text-xl mb-2">{eventName}</h4>
              <p className="text-sm md:text-base mb-2">{eventLocation}</p>
              <p className="text-sm md:text-base mb-3">{formatFirestoreDate(selectedSlot.startTime)} | {convertTo12HourFormat(selectedSlot.startTime)} - {convertTo12HourFormat(selectedSlot.endTime)}</p>
              <div className="ticket-count-row flex items-center">
                <img src={Ticket} alt="ticket" className="ticket-icon w-4 h-4 md:w-5 md:h-5 mr-2" />
                <span className="text-sm md:text-base">M - Ticket available</span>
              </div>

              {/* Show selected tickets */}
              <div className="selected-tickets-summary mt-4">
                <h5 className="text-base md:text-lg mb-3">Selected Tickets:</h5>
                {Object.entries(selectedTickets).map(([ticketId, quantity]) => {
                  const ticket = selectedSlot.tickets.find(t => t.id === ticketId);
                  return ticket ? (
                    <div key={ticketId} className="ticket-summary-item flex justify-between items-center py-2 text-sm md:text-base">
                      <span>{ticket.name} x {quantity}</span>
                      <span className="font-semibold">₹{(ticket.price * quantity).toFixed(2)}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>

            {/* Mobile-only sections - Important Points and Ticket Details */}
            <div className="block lg:hidden mb-4">
              {/* Important Points for Mobile */}
              <div className="important-points mb-6">
                <h4 className="text-base md:text-lg mb-3">Important Points</h4>
                <div className="point mb-2 text-sm md:text-base">
                  <span className="bullet mr-2">&#8226;</span>
                  <span>Customers will get tickets in their profile section</span>
                </div>
                <div className="point mb-2 text-sm md:text-base">
                  <span className="bullet mr-2">&#8226;</span>
                  <span>Bring a government ID for entry</span>
                </div>
                <div className="point mb-2 text-sm md:text-base">
                  <span className="bullet mr-2">&#8226;</span>
                  <span>Show ticket QR code at entry</span>
                </div>
              </div>

              {/* Ticket Details for Mobile */}
              <div className="ticket-details-box mb-6">
                <div className="ticket-details-header mb-4">
                  <h4 className="text-base md:text-lg">Ticket Details</h4>
                </div>

                {userDataLoading ? (
                  <div className="p-4 md:p-5 text-center text-gray-500 italic text-sm md:text-base">
                    Loading your details...
                  </div>
                ) : userDataError ? (
                  <div className="p-4 md:p-5 text-center text-red-600 text-sm mb-4">
                    <p>⚠️ {userDataError}</p>
                    <p className="text-xs md:text-sm mt-2">
                      Please fill in your details below
                    </p>
                  </div>
                ) : null}

                <div className="ticket-form-fields grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Name Field */}
                  <div
                    className={`w-full p-3 !bg-black/40 rounded-md text-sm min-h-5 box-border ${userDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => handleFieldClick('name')}
                  >
                    {editingField === 'name' ? (
                      <input
                        type="text"
                        value={ticketDetails.name}
                        onChange={(e) => setTicketDetails({ ...ticketDetails, name: e.target.value })}
                        onBlur={handleFieldBlur}
                        onKeyPress={handleKeyPress}
                        autoFocus
                        className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-0 m-0 !text-white"
                      />
                    ) : (
                      <span className={ticketDetails.name ? 'text-white' : 'text-gray-300'}>
                        {ticketDetails.name || 'Click to enter Full Name *'}
                      </span>
                    )}
                  </div>

                  {/* Email Field */}
                  <div
                    className={`w-full p-3 !bg-black/40 rounded-md text-sm min-h-5 box-border ${userDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => handleFieldClick('email')}
                  >
                    {editingField === 'email' ? (
                      <input
                        type="email"
                        value={ticketDetails.email}
                        onChange={(e) => setTicketDetails({ ...ticketDetails, email: e.target.value })}
                        onBlur={handleFieldBlur}
                        onKeyPress={handleKeyPress}
                        autoFocus
                        className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-0 m-0 !text-white"
                      />
                    ) : (
                      <span className={ticketDetails.email ? 'text-white' : 'text-gray-300'}>
                        {ticketDetails.email || 'Click to enter Email Address *'}
                      </span>
                    )}
                  </div>

                  {/* Phone Field */}
                  <div
                    className={`w-full p-3 !bg-black/40 rounded-md text-sm min-h-5 box-border ${userDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => handleFieldClick('phone')}
                  >
                    {editingField === 'phone' ? (
                      <input
                        type="tel"
                        value={ticketDetails.phone}
                        onChange={(e) => setTicketDetails({ ...ticketDetails, phone: e.target.value })}
                        onBlur={handleFieldBlur}
                        onKeyPress={handleKeyPress}
                        autoFocus
                        className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-0 m-0 !text-white"
                      />
                    ) : (
                      <span className={ticketDetails.phone ? 'text-white' : 'text-gray-300'}>
                        {ticketDetails.phone || 'Click to enter Phone Number *'}
                      </span>
                    )}
                  </div>

                  {/* State Field */}
                  <div className="w-full !bg-black/40 rounded-md text-sm min-h-5 box-border">
                    <select
                      value={ticketDetails.state}
                      onChange={(e) => setTicketDetails({ ...ticketDetails, state: e.target.value })}
                      disabled={userDataLoading}
                      className="w-full border-none outline-none !bg-black/0 text-sm appearance-none shadow-none p-3 m-0 !text-white rounded-md cursor-pointer disabled:cursor-not-allowed"
                    >
                      <option value="" className="!bg-gray-800 !text-white">Select State *</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state} className="!bg-gray-800 !text-white">
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Coupon Input Component */}
            <CouponInput
              onCouponApplied={handleCouponApplied}
              onCouponRemoved={handleCouponRemoved}
              appliedCoupon={appliedCoupon}
              subtotal={subtotal}
              userId={user?.uid}
              eventId={eventId}
              disabled={isProcessingPayment || userDataLoading}
            />

            <div className="payment-summary-box p-4 mb-4">
              <h4 className="text-base md:text-lg mb-3">Payment Summary</h4>
              <div className="vertical-separator mb-3"></div>

              <div className="summary-row flex justify-between items-center py-2 text-sm md:text-base">
                <span>Subtotal</span>
                <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
              </div>

              {/* Coupon Discount Row */}
              {appliedCoupon && (
                <div className="summary-row flex justify-between items-center py-2 text-sm md:text-base">
                  <span className="text-green-400">
                    Coupon Discount ({appliedCoupon.couponCode})
                  </span>
                  <span className="font-semibold text-green-400">
                    -₹{appliedCoupon.discountAmount.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="booking-fee-container py-2 cursor-pointer" onClick={() => setShowFeeDetails(!showFeeDetails)}>
                <div className="booking-fee-header flex justify-between items-center">
                  <span className="text-sm md:text-base">Booking Fee</span>
                  <div className={`arrow-icon ${showFeeDetails ? 'rotate' : ''}`}>
                    <img src={arrowDown} alt="Dropdown Arrow" className="w-4 h-4" />
                  </div>
                </div>
                {showFeeDetails && (
                  <div className="booking-fee-details mt-2 ml-2">
                    <div className="fee-line flex justify-between items-center py-1 text-xs md:text-sm">
                      <span>Convenience Fee</span>
                      <span>₹{adjustedConvenienceFee.toFixed(2)}</span>
                    </div>
                    <div className="fee-line flex justify-between items-center py-1 text-xs md:text-sm">
                      <span>IGST (18%)</span>
                      <span>₹{adjustedIgst.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="vertical-separator my-3"></div>
              <div className="summary-row total flex justify-between items-center py-2 text-base md:text-lg font-bold">
                <span>Grand Total</span>
                <span>₹{grandTotal.toFixed(2)}</span>
              </div>
            </div>

            <a href="#" className="help-link text-sm md:text-base text-center block py-2">Need Support? Help?</a>

            {/* Payment Error Display */}
            {paymentError && (
              <div className="payment-error-message bg-red-100 border border-red-200 text-red-600 p-3 md:p-4 rounded-lg mb-4 text-xs md:text-sm">
                <strong>Payment Error:</strong> {paymentError}
                <button
                  onClick={() => setPaymentError(null)}
                  className="float-right bg-none border-none text-red-600 cursor-pointer text-lg md:text-xl font-bold ml-2"
                >
                  ×
                </button>
              </div>
            )}

            <button
              className="proceed-btn w-full py-3 md:py-4 text-base md:text-lg font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleProceed}
              disabled={isProcessingPayment || userDataLoading}
            >
              {userDataLoading ? "Loading Details..." : (isProcessingPayment ? "Processing..." : "Proceed")}
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================== */}
      {/* MODALS */}
      {/* ========================================================================== */}



      {/* Payment Loading Modal */}
      <PaymentLoadingModal isVisible={showPaymentModal} />

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isVisible={paymentStatus === 'success'}
        bookingId={bookingId}
      />
    </>
  );
};

export default Checkout;




